import { computed, ref, watch } from 'vue'
import { useCompanyStore } from '@/stores/company'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import {
  canCreateShipments,
  CLIENT_PLAN,
  companyHasAI,
  FREE_PREMIUM_SHIPMENT_LIMIT,
  getShipmentsUsed,
  resolveCompanyPlan
} from '@/utils/companyPlan'

// Compartido entre pantallas: crear un embarque en una refresca el uso en todas
const shipmentsUsed = ref<number | null>(null)
const usageCompanyId = ref<string | null>(null)

/** Plan de la compañía seleccionada y su cupo de embarques (ver utils/companyPlan). */
export function useCompanyPlan() {
  const companyStore = useCompanyStore()
  const company = computed(() => companyStore.currentCompanyData)

  const plan = computed(() => resolveCompanyPlan(company.value))
  const isClientPlan = computed(() => plan.value === CLIENT_PLAN)
  const hasAI = computed(() => companyHasAI(company.value))

  const refreshShipmentUsage = async () => {
    const companyId = company.value?.id
    if (!companyId) return
    usageCompanyId.value = companyId
    if (isClientPlan.value) return
    try {
      const used = await getShipmentsUsed(FirebaseProvider.getInstance().getFirestore(), companyId)
      if (usageCompanyId.value === companyId) shipmentsUsed.value = used
    } catch (error) {
      console.error('Error cargando el uso de embarques de la compañía:', error)
    }
  }

  // Mientras no esté el uso de ESTA compañía no se bloquea: el repositorio y los
  // servicios de tracking validan de nuevo al crear.
  const hasReachedShipmentsLimit = computed(() =>
    !!company.value &&
    !isClientPlan.value &&
    usageCompanyId.value === company.value.id &&
    shipmentsUsed.value !== null &&
    !canCreateShipments(plan.value, shipmentsUsed.value)
  )

  watch(() => company.value?.id, (id) => {
    if (!id || id === usageCompanyId.value) return
    shipmentsUsed.value = null
    refreshShipmentUsage()
  }, { immediate: true })

  return {
    plan,
    isClientPlan,
    hasAI,
    shipmentsUsed: computed(() => shipmentsUsed.value ?? 0),
    shipmentLimit: FREE_PREMIUM_SHIPMENT_LIMIT,
    hasReachedShipmentsLimit,
    refreshShipmentUsage
  }
}
